import { FC, Fragment, useState } from 'react';

import {
    BodyText,
    Button,
    ButtonGroup,
    Checkbox,
    Divider,
    Modal,
    Stack,
} from '@servicetitan/design-system';

import { RadioButtonGroup } from '../../common';

export enum AppPurpose {
    OwnOrg = 'own-org',
    ThirdParty = 'third-party',
}

interface ThirdPartyGateDialogProps {
    onContinue: (appPurpose: AppPurpose) => void;
    onCancel: () => void;
}

export const ThirdPartyGateDialog: FC<ThirdPartyGateDialogProps> = ({ onContinue, onCancel }) => {
    const [appPurpose, setAppPurpose] = useState<AppPurpose | undefined>(undefined);
    const [termsAccepted, setTermsAccepted] = useState(false);

    return (
        <Modal
            open
            size={Modal.Sizes.S}
            title="Register New App"
            onClose={onCancel}
            footer={
                <ButtonGroup>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button
                        primary
                        disabled={
                            !appPurpose || (appPurpose === AppPurpose.OwnOrg && !termsAccepted)
                        }
                        onClick={() => onContinue(appPurpose!)}
                    >
                        Continue
                    </Button>
                </ButtonGroup>
            }
        >
            <Stack direction="column">
                <BodyText>Who will be accessing the API endpoints/webhooks?</BodyText>
                <RadioButtonGroup<AppPurpose>
                    name="app-build-intent"
                    value={appPurpose}
                    options={[
                        {
                            label: 'A third party (another software organization)',
                            value: AppPurpose.ThirdParty,
                        },
                        {
                            label: 'Our organization',
                            value: AppPurpose.OwnOrg,
                        },
                    ]}
                    onChange={(_event, data) => {
                        setAppPurpose(data.value);
                        setTermsAccepted(false);
                    }}
                />
                <BodyText>
                    <strong>Why this matters:</strong> Apps have access to your business’s data.
                    ServiceTitan adds extra security to third-party apps to keep your data safe.
                </BodyText>
                {appPurpose === AppPurpose.OwnOrg && (
                    <Fragment>
                        <Divider spacing="2" />
                        <BodyText className="m-b-1">
                            Please confirm that you’re not registering this app for a third party to
                            connect to you — as this exposes your business data and violates our{' '}
                            <a
                                href="https://www.servicetitan.com/legal/api-terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="Link Link--blue"
                            >
                                Terms of Use
                            </a>
                            .
                        </BodyText>
                        <Checkbox
                            label="I confirm this app is being hosted and maintained by my organization"
                            checked={termsAccepted}
                            onChange={(_value, checked) => setTermsAccepted(checked)}
                        />
                    </Fragment>
                )}
            </Stack>
        </Modal>
    );
};
ThirdPartyGateDialog.displayName = 'ThirdPartyGateDialog';
